/**
 * Single Axios client used by every page. Mirrors the Flutter
 * `ApiClient` so the contract on both sides stays identical: bearer
 * token from localStorage, JSON body, success/error envelope `{success,
 * message, data, errors}`.
 */

import axios, { type AxiosError, type AxiosInstance } from 'axios';

const TOKEN_KEY = 'billbaaki.web.token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

// Production: SPA is served from the same Laravel host (`/panel`), so
// every `/api/...` call is same-origin → no baseURL needed.
// Dev: Vite proxies `/api` to VITE_API_BASE, also same-origin from the
// browser's perspective. So either way, baseURL stays empty.
export const api: AxiosInstance = axios.create({
  baseURL: '',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  timeout: 30_000,
});

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

/** 401 → wipe token and let the route guard bounce to /login. */
api.interceptors.response.use(
  (r) => r,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      setToken(null);
      // Don't redirect here — pages decide. Just clear the token.
    }
    return Promise.reject(error);
  },
);

/** Convenience: unwrap the `{success, data}` envelope and surface the
 * server's message when `success === false`. */
export async function apiGet<T = unknown>(path: string, params?: Record<string, unknown>): Promise<T> {
  const r = await api.get(path, { params });
  return unwrap<T>(r.data);
}
export async function apiPost<T = unknown>(path: string, body?: unknown): Promise<T> {
  const r = await api.post(path, body);
  return unwrap<T>(r.data);
}
export async function apiPut<T = unknown>(path: string, body?: unknown): Promise<T> {
  const r = await api.put(path, body);
  return unwrap<T>(r.data);
}
export async function apiDelete<T = unknown>(path: string): Promise<T> {
  const r = await api.delete(path);
  return unwrap<T>(r.data);
}

function unwrap<T>(body: unknown): T {
  if (body && typeof body === 'object' && 'success' in body) {
    const env = body as { success: boolean; data?: T; message?: string; errors?: unknown };
    if (env.success === false) {
      const err = new Error(env.message || 'Request failed');
      (err as Error & { details?: unknown }).details = env.errors;
      throw err;
    }
    return (env.data as T) ?? (body as T);
  }
  return body as T;
}
